// La mascota del Agente en la conversación —
// ADR-2026-09-12-la-mascota-acompana-la-conversacion y
// ADR-2026-09-12-la-mascota-aterriza-en-vez-de-cortar.
//
// Cumbre ya recibía en el arranque (grande, quieta, pestañeando cada tanto). Ahora
// también acompaña la conversación, chiquita, debajo del último mensaje: quieta mientras
// el agente espera, y en cuanto el usuario manda un mensaje arranca a cruzar y girar —el
// gesto del "Pensando…" de Asistente v2— pero en una PISTA CORTA, del ancho de la
// palabra "Pensando…", no de borde a borde. Y al pasar el mouse (o enfocarla con el
// teclado) saluda: un easter egg, no un control.
//
// ES UNA SOLA MASCOTA EN LOS DOS ESTADOS. Antes el viaje eran keyframes CSS
// (`.cumbre-viaje` / `.cumbre-giro`) y al terminar el turno se desmontaba el elemento
// que cruzaba y se montaba otro quieto en el inicio: un salto de posición y de giro. Acá
// el cruce y el giro van por TRANSICIONES sobre un contador de tramos (`leg`): cada
// tramo es un cruce de la pista más una vuelta entera, y cuando el trabajo termina la
// mascota completa el tramo que esté haciendo y vuelve al inicio con la misma cadencia
// antes de quedarse quieta. Los ojos cambian con `data-state` y una transición corta en
// `cumbre.css`. Con `prefers-reduced-motion` no viaja: sólo cambia de ojos.

import { useEffect, useRef, useState } from 'react';
import { CumbreMascot, type CumbreMascotProps } from '../../components/CumbreMascot';

export const SALUDO_DE_LA_MASCOTA = '¡Hola! Soy el asistente de Atlas. ¿En qué te puedo ayudar?';

/** El ancho de la pista del viaje: más o menos lo que mide "Pensando…" a 15px. */
export const ANCHO_PISTA_PX = 112;

/** Cuánto dura un tramo (un cruce de la pista y una vuelta sobre su eje). Rápido:
 *  la pista es corta y a 3,6 s por tramo —lo de v2 a lo ancho— se veía en cámara lenta. */
export const MS_POR_TRAMO = 1400;

const GRADIENTE_ESPERA = 'linear-gradient(90deg, var(--brand-primary) 25%, var(--brand-accent) 50%, var(--brand-primary) 75%)';

/**
 * La mascota con el saludo. El wrapper es enfocable (`tabIndex`) para que el saludo
 * también salga con el teclado; la burbuja es `role="tooltip"` y no recibe clicks.
 * `arriba` para la grande del arranque (centrada); `derecha` para la chica de la
 * conversación (alineada a la izquierda, con lugar al lado). `saludo` en false la deja
 * sin burbuja (mientras viaja no hay a qué apuntar).
 */
export const MascotaConSaludo: React.FC<{
  size: number;
  className?: string;
  burbuja?: 'derecha' | 'arriba';
  state?: CumbreMascotProps['state'];
  saludo?: boolean;
}> = ({ size, className, burbuja = 'derecha', state = 'idle', saludo = true }) => (
  <span
    className="group relative inline-flex shrink-0 outline-none"
    tabIndex={0}
    role="img"
    aria-label="Cumbre, la mascota de Atlas"
  >
    <CumbreMascot state={state} size={size} decorativa className={className} />
    {saludo && (
      <span
        role="tooltip"
        // `w-max max-w-[260px]` y NO `whitespace-nowrap`: la burbuja está montada
        // siempre (con `opacity-0`, para que la transición ande) y en una sola línea
        // mide ~400px; en el panel lateral se pasaba del ancho de la columna y, como
        // los transforms cuentan para el área scrolleable, el `overflow-y-auto` del
        // contenedor ofrecía un scroll horizontal de unos píxeles. A 260px envuelve en
        // dos líneas y entra en cualquier columna (ADR-2026-09-12-la-burbuja-de-la-
        // mascota-no-ensancha-el-panel). El ancho va explícito porque un `absolute`
        // sin ancho se encoge al de su contenedor (la mascota) y partiría cada palabra.
        className={`pointer-events-none absolute z-10 w-max max-w-[260px] whitespace-normal rounded-2xl bg-white px-3.5 py-2 text-[13px] font-medium leading-snug text-brand-black opacity-0 shadow-[0_8px_24px_rgb(var(--brand-black-rgb)_/_0.14)] ring-1 ring-[rgb(var(--brand-black-rgb)_/_0.06)] transition-[opacity,transform] duration-200 group-hover:opacity-100 group-focus-visible:opacity-100 ${
          burbuja === 'arriba'
            ? 'bottom-full left-1/2 mb-2 -translate-x-1/2 translate-y-1 text-center group-hover:translate-y-0 group-focus-visible:translate-y-0'
            : 'left-full top-1/2 ml-2 -translate-y-1/2 -translate-x-1 rounded-bl-md text-left group-hover:translate-x-0 group-focus-visible:translate-x-0'
        }`}
      >
        {SALUDO_DE_LA_MASCOTA}
      </span>
    )}
  </span>
);

/**
 * La mascota al pie de la conversación. `quieta`: pestañea y espera. `trabajando`: cruza
 * y gira en la pista corta, con la etiqueta de lo que está haciendo ARRIBA (en el
 * gradiente de marca) cuando la hay. El paso de un modo al otro no corta nada: termina
 * el tramo, vuelve al inicio y recién ahí descansa.
 */
export const MascotaAgente: React.FC<{
  modo: 'quieta' | 'trabajando';
  etiqueta?: string | null;
}> = ({ modo, etiqueta }) => {
  const trabajando = modo === 'trabajando';
  // Se lee una vez al montar; para quien pidió menos movimiento el viaje no aporta.
  const [animar] = useState(
    () => typeof window !== 'undefined' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  // Tramos INICIADOS. Impar = yendo (o llegado) al extremo; par = volviendo (o llegado)
  // al inicio. Cada tramo suma una vuelta entera. El ref es la fuente de verdad del
  // intervalo (que no ve el estado); el estado es lo que dibuja.
  const legRef = useRef(0);
  const [leg, setLeg] = useState(0);
  // Sigue en movimiento mientras trabaja, y si dejó de trabajar sólo hasta volver al inicio.
  const activo = trabajando || leg % 2 === 1;

  useEffect(() => {
    if (!animar || !activo) return;
    const avanzar = () => {
      legRef.current += 1;
      setLeg(legRef.current);
    };
    // En reposo y con trabajo: el primer tramo arranca ya, no dentro de 1,4 s.
    if (legRef.current % 2 === 0) avanzar();
    const id = window.setInterval(avanzar, MS_POR_TRAMO);
    // Cuando el tramo de vuelta termina con el trabajo ya terminado, `activo` pasa a
    // false y este cleanup frena el intervalo. La transición en curso termina sola.
    return () => window.clearInterval(id);
  }, [animar, activo]);

  const enElExtremo = leg % 2 === 1;
  const transicion = `${MS_POR_TRAMO}ms`;

  return (
    <div className="py-1">
      <style>{`@keyframes atlasThinkShimmer { to { background-position: -200% 0 } }`}</style>
      {/* Lo que está haciendo va ARRIBA y la mascota debajo: así queda pegada a la acción en
          curso, debajo de las líneas de lo que ya hizo
          (ADR-2026-10-22-cada-accion-del-asistente-queda-en-una-linea-2). */}
      {trabajando && etiqueta && (
        <div className="mb-1.5">
          <span
            className="bg-clip-text text-[15px] font-semibold text-transparent"
            style={{
              backgroundImage: GRADIENTE_ESPERA,
              backgroundSize: '200% 100%',
              animation: 'atlasThinkShimmer 1.4s linear infinite',
            }}
          >
            {etiqueta}
          </span>
        </div>
      )}
      {/* La pista: `relative` + alto fijo (el que cruza es `absolute` y no aporta alto)
          y `overflow-visible` para que la sombra se salga en los extremos. */}
      <div className="relative h-[34px] overflow-visible" style={{ width: ANCHO_PISTA_PX }}>
        {/* Tres capas, como en `cumbre.css`, porque `transform` es una sola propiedad:
            el cruce (translateX + left), el giro (rotate) y adentro el float de `.cumbre`.
            `left: 100%` + `translateX(-100%)` deja el borde derecho de la mascota justo
            en el borde de la pista sin medir nada. */}
        <span
          className="absolute top-0 block"
          style={{
            left: enElExtremo ? '100%' : 0,
            transform: enElExtremo ? 'translateX(-100%)' : 'translateX(0)',
            transition: `left ${transicion} ease-in-out, transform ${transicion} ease-in-out`,
          }}
        >
          <span
            className="block"
            style={{
              transform: `rotate(${leg * 360}deg)`,
              transition: `transform ${transicion} linear`,
            }}
          >
            <MascotaConSaludo
              size={34}
              className="cumbre--mini h-auto w-[34px]"
              burbuja="derecha"
              state={trabajando ? 'thinking' : 'idle'}
              saludo={!activo}
            />
          </span>
        </span>
      </div>
    </div>
  );
};

export default MascotaAgente;
